import { throttle } from "lodash-es"

type SerialFetchHandlers = {
  onRejected?: (error: any) => void
  onResolved?: (result: any) => void
  onInvoked?: () => void
  onAborted?: () => void
}

export type SerialFetch = {
  (getRequest: () => any, handlers?: SerialFetchHandlers): void
}

function createSearchRequest(url: string, body: any) {
  return new Request(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  })
}

function createSerialFetch(
  url: string,
  fetch: typeof globalThis.fetch = window.fetch,
): SerialFetch {
  let abortController: AbortController = new AbortController()

  return async (
    getRequestBody: () => any,
    {
      onRejected = (_error: any) => {},
      onResolved = (_result: any) => {},
      onInvoked = () => {},
      onAborted = () => {},
    } = {},
  ) => {
    let settled = false

    type Callback = (...args: any[]) => void
    const settleWith: (callback: Callback) => Callback =
      (callback) =>
      (...args: any[]) => {
        if (settled) return

        settled = true
        callback(...args)
      }

    abortController.abort()
    abortController = new AbortController()
    const signal = abortController.signal

    await Promise.resolve()

    if (signal.aborted) return

    signal.addEventListener("abort", async () => {
      await Promise.resolve()
      settleWith(onAborted)()
    })

    const request = createSearchRequest(url, getRequestBody())
    fetch(request, { signal })
      .then((response) => {
        if (signal.aborted) return

        return response.json()
      })
      .then(settleWith(onResolved), settleWith(onRejected))

    onInvoked()
  }
}

export type SearchClientOptions = { queryThrottle?: number; url: string }
export const createSearchClient = (
  { queryThrottle, url }: SearchClientOptions,
  globalFetch: typeof globalThis.fetch = window.fetch,
) =>
  throttle(createSerialFetch(url, globalFetch), queryThrottle, {
    leading: true,
  })

export default createSearchClient

export type SearchClient = ReturnType<typeof createSearchClient>
